// Copyright (c) 2026 Aravind Adari. All rights reserved.

import { useState, useEffect } from "react";
import { getIdTokenResult } from "firebase/auth";
import {
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { auth } from "../firebase";
import { useClient } from "../contexts/ClientContext";
import {
  Box,
  Button,
  Typography,
  TextField,
  Snackbar,
  Alert,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  LinearProgress,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Chip,
  Tooltip,
  Badge,
  TablePagination,
} from "@mui/material";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import SettingsIcon from "@mui/icons-material/Settings";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import dayjs, { Dayjs } from "dayjs";
import usePagination from "../hooks/usePagination";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

/* ─── Types ─── */
interface Employee {
  id: string;
  empId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  designation: string;
  baseSalary: number;
  status: string;
  branchId: string;
  settlementAccountId: string;
  allowances: Record<string, number>;
  tdAllowancePct: number;
}
interface AdvanceRow {
  id?: string;
  empId: string;
  amount: number;
  date: string;
  note?: string;
  createdAt?: Timestamp;
}
interface AllowanceType {
  id: string;
  name: string;
  amount: number;
}
interface SettlementAccount {
  id: string;
  name: string;
}
interface LocationItem {
  id: string;
  name: string;
}
interface ChangeRequest {
  id: string;
  type: string;
  attendanceId: string;
  empId: string;
  requestedBy: string;
  reason: string;
  status: string;
  createdAt: Timestamp;
}

export default function SettingsPage() {
  const { col, docRef } = useClient();
  const [settingsTab, setSettingsTab] = useState<"advances" | "payroll" | "admin">("advances");
  const [role, setRole] = useState("");
  const [month, setMonth] = useState(() => dayjs().format("YYYY-MM"));
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [allowanceTypes, setAllowanceTypes] = useState<AllowanceType[]>([]);
  const [settlementAccounts, setSettlementAccounts] = useState<SettlementAccount[]>([]);
  const [locations, setLocations] = useState<LocationItem[]>([]);
  const [advances, setAdvances] = useState<AdvanceRow[]>([]);
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState<"success" | "error" | "info">("success");
  const [loading, setLoading] = useState(false);

  // Advance CRUD
  const [advOpen, setAdvOpen] = useState(false);
  const [advEmpId, setAdvEmpId] = useState("");
  const [advAmount, setAdvAmount] = useState(0);
  const [advDate, setAdvDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [advNote, setAdvNote] = useState("");

  // Payroll Config
  const [payrollEditId, setPayrollEditId] = useState<string | null>(null);
  const [payrollForm, setPayrollForm] = useState<{
    tdAllowancePct: number;
    settlementAccountId: string;
    allowances: Record<string, number>;
  }>({ tdAllowancePct: 10, settlementAccountId: "", allowances: {} });
  const [payrollLocFilter, setPayrollLocFilter] = useState("");

  // Admin settings dialogs
  const [atOpen, setAtOpen] = useState(false);
  const [atName, setAtName] = useState("");
  const [atAmount, setAtAmount] = useState(0);
  const [atEditId, setAtEditId] = useState<string | null>(null);
  const [saOpen, setSaOpen] = useState(false);
  const [saName, setSaName] = useState("");
  const [saEditId, setSaEditId] = useState<string | null>(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifMessage, setNotifMessage] = useState("");
  const [notifTarget, setNotifTarget] = useState("receptionist");

  // Pagination
  const advPag = usePagination(advances);
  const payrollEmps = employees.filter((e) => e.status === "active").filter((e) => !payrollLocFilter || e.branchId === payrollLocFilter);
  const payConfPag = usePagination(payrollEmps);
  const crPag = usePagination(changeRequests);

  const showMsg = (m: string, t: "success" | "error" | "info" = "success") => {
    setMsg(m);
    setMsgType(t);
  };

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    getIdTokenResult(user)
      .then((r) => setRole((r.claims.role as string) ?? ""))
      .catch(() => setRole(""));
  }, []);

  /* ─── Load base data ─── */
  useEffect(() => {
    async function loadBase() {
      try {
        const [empSnap, atSnap, saSnap, locSnap, crSnap] = await Promise.all([
          getDocs(col("employees")),
          getDocs(col("allowanceTypes")),
          getDocs(col("settlementAccounts")),
          getDocs(col("branches")),
          getDocs(col("changeRequests")),
        ]);
        setEmployees(
          empSnap.docs.map((d) => {
            const data = d.data() as Record<string, unknown>;
            return {
              id: d.id,
              empId: (data.empId as string) ?? d.id,
              firstName: (data.firstName as string) ?? "",
              lastName: (data.lastName as string) ?? "",
              fullName: (data.fullName as string) ?? "",
              designation: (data.designation as string) ?? (data.role as string) ?? "",
              baseSalary: Number(data.baseSalary ?? 0),
              status: (data.status as string) ?? "active",
              branchId: (data.branchId as string) ?? "",
              settlementAccountId: (data.settlementAccountId as string) ?? "",
              allowances: (data.allowances as Record<string, number>) ?? {},
              tdAllowancePct: Number(data.tdAllowancePct ?? 10),
            };
          })
        );
        setAllowanceTypes(
          atSnap.docs.map((d) => ({
            id: d.id,
            name: (d.data().name as string) ?? "",
            amount: Number(d.data().amount ?? 0),
          }))
        );
        setSettlementAccounts(
          saSnap.docs.map((d) => ({ id: d.id, name: (d.data().name as string) ?? "" }))
        );
        setLocations(
          locSnap.docs
            .map((d) => ({ id: d.id, name: (d.data().name as string) ?? "" }))
            .sort((a, b) => {
              if (a.name.toLowerCase().startsWith("anakapalli")) return -1;
              if (b.name.toLowerCase().startsWith("anakapalli")) return 1;
              return a.name.localeCompare(b.name);
            })
        );
        setChangeRequests(
          crSnap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ChangeRequest, "id">) }))
        );
      } catch {
        showMsg("Failed to load data.", "error");
      }
    }
    loadBase();
  }, []);

  /* ─── Load advances for selected month ─── */
  const reloadAdvances = async () => {
    const startStr = dayjs(month + "-01").format("YYYY-MM-DD");
    const endStr = dayjs(month + "-01").endOf("month").format("YYYY-MM-DD");
    setLoading(true);
    try {
      const advQ = query(
        col("advances"),
        where("date", ">=", startStr),
        where("date", "<=", endStr)
      );
      const advSnap = await getDocs(advQ);
      setAdvances(advSnap.docs.map((d) => ({ id: d.id, ...(d.data() as AdvanceRow) })));
    } catch {
      setAdvances([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadAdvances();
    // eslint-disable-next-line
  }, [month]);

  const getEmpName = (id: string) => {
    const emp = employees.find((e) => e.id === id || e.empId === id);
    if (!emp) return id;
    return emp.firstName && emp.lastName
      ? `${emp.firstName} ${emp.lastName}`
      : emp.fullName || emp.empId;
  };

  /* ─── Advance CRUD ─── */
  const saveAdvance = async () => {
    if (!advEmpId) return showMsg("Please select an employee.", "error");
    if (advAmount <= 0) return showMsg("Amount must be greater than zero.", "error");
    if (!advDate) return showMsg("Please select a date.", "error");
    try {
      await addDoc(col("advances"), {
        empId: advEmpId,
        amount: advAmount,
        date: advDate,
        note: advNote.trim(),
        createdAt: Timestamp.now(),
      });
      showMsg("Advance recorded.");
      setAdvOpen(false);
      reloadAdvances();
    } catch {
      showMsg("Failed to save advance.", "error");
    }
  };

  const deleteAdvance = async (id: string) => {
    if (!window.confirm("Delete this advance?")) return;
    try {
      await deleteDoc(docRef("advances", id));
      showMsg("Advance deleted.");
      reloadAdvances();
    } catch {
      showMsg("Failed to delete advance.", "error");
    }
  };

  /* ─── Payroll Config ─── */
  const openPayrollEdit = (emp: Employee) => {
    const mergedAllowances: Record<string, number> = {};
    allowanceTypes.forEach((at) => {
      mergedAllowances[at.id] = emp.allowances[at.id] ?? at.amount ?? 0;
    });
    setPayrollEditId(emp.id);
    setPayrollForm({
      tdAllowancePct: emp.tdAllowancePct ?? 10,
      settlementAccountId: emp.settlementAccountId ?? "",
      allowances: mergedAllowances,
    });
  };

  const savePayrollConfig = async () => {
    if (!payrollEditId) return;
    try {
      await updateDoc(docRef("employees", payrollEditId), {
        tdAllowancePct: payrollForm.tdAllowancePct,
        settlementAccountId: payrollForm.settlementAccountId,
        allowances: payrollForm.allowances,
      });
      setPayrollEditId(null);
      showMsg("Payroll config saved.");
      // Reload employees to reflect changes
      const empSnap = await getDocs(col("employees"));
      setEmployees(
        empSnap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          return {
            id: d.id,
            empId: (data.empId as string) ?? d.id,
            firstName: (data.firstName as string) ?? "",
            lastName: (data.lastName as string) ?? "",
            fullName: (data.fullName as string) ?? "",
            designation: (data.designation as string) ?? "",
            baseSalary: Number(data.baseSalary ?? 0),
            status: (data.status as string) ?? "active",
            branchId: (data.branchId as string) ?? "",
            settlementAccountId: (data.settlementAccountId as string) ?? "",
            allowances: (data.allowances as Record<string, number>) ?? {},
            tdAllowancePct: Number(data.tdAllowancePct ?? 10),
          };
        })
      );
    } catch {
      showMsg("Failed to save payroll config.", "error");
    }
  };

  const loadAllowanceTypes = async () => {
    try {
      const snap = await getDocs(col("allowanceTypes"));
      setAllowanceTypes(
        snap.docs.map((d) => ({
          id: d.id,
          name: (d.data().name as string) ?? "",
          amount: Number(d.data().amount ?? 0),
        }))
      );
    } catch {
      showMsg("Failed to load allowance types.", "error");
    }
  };

  const loadSettlementAccounts = async () => {
    try {
      const snap = await getDocs(col("settlementAccounts"));
      setSettlementAccounts(snap.docs.map((d) => ({ id: d.id, name: (d.data().name as string) ?? "" })));
    } catch {
      showMsg("Failed to load settlement accounts.", "error");
    }
  };

  const loadChangeRequests = async () => {
    try {
      const snap = await getDocs(col("changeRequests"));
      setChangeRequests(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ChangeRequest, "id">) })));
    } catch {
      showMsg("Failed to load change requests.", "error");
    }
  };

  const openAtDialog = (at?: AllowanceType) => {
    setAtEditId(at?.id ?? null);
    setAtName(at?.name ?? "");
    setAtAmount(at?.amount ?? 0);
    setAtOpen(true);
  };

  const saveAllowanceType = async () => {
    if (!atName.trim()) return showMsg("Allowance name is required.", "error");
    if (atAmount < 0) return showMsg("Amount cannot be negative.", "error");
    try {
      const data = { name: atName.trim(), amount: atAmount };
      if (atEditId) {
        await updateDoc(docRef("allowanceTypes", atEditId), data);
      } else {
        await addDoc(col("allowanceTypes"), data);
      }
      setAtOpen(false);
      showMsg(atEditId ? "Allowance type updated." : "Allowance type added.");
      loadAllowanceTypes();
    } catch {
      showMsg("Failed to save allowance type.", "error");
    }
  };

  const deleteAllowanceType = async (id: string) => {
    if (!window.confirm("Delete this allowance type?")) return;
    try {
      await deleteDoc(docRef("allowanceTypes", id));
      showMsg("Allowance type deleted.");
      loadAllowanceTypes();
    } catch {
      showMsg("Failed to delete allowance type.", "error");
    }
  };

  const openSaDialog = (sa?: SettlementAccount) => {
    setSaEditId(sa?.id ?? null);
    setSaName(sa?.name ?? "");
    setSaOpen(true);
  };

  const saveSettlementAccount = async () => {
    if (!saName.trim()) return showMsg("Account name is required.", "error");
    try {
      const data = { name: saName.trim() };
      if (saEditId) {
        await updateDoc(docRef("settlementAccounts", saEditId), data);
      } else {
        await addDoc(col("settlementAccounts"), data);
      }
      setSaOpen(false);
      showMsg(saEditId ? "Account updated." : "Account added.");
      loadSettlementAccounts();
    } catch {
      showMsg("Failed to save account.", "error");
    }
  };

  const deleteSettlementAccount = async (id: string) => {
    if (!window.confirm("Delete this account?")) return;
    try {
      await deleteDoc(docRef("settlementAccounts", id));
      showMsg("Account deleted.");
      loadSettlementAccounts();
    } catch {
      showMsg("Failed to delete account.", "error");
    }
  };

  const resolveChangeRequest = async (crId: string, status: "approved" | "rejected") => {
    try {
      await updateDoc(docRef("changeRequests", crId), { status });
      showMsg(`Change request ${status}.`);
      loadChangeRequests();
    } catch {
      showMsg("Failed to update change request.", "error");
    }
  };

  const sendNotification = async () => {
    if (!notifMessage.trim()) return showMsg("Notification message is required.", "error");
    try {
      await addDoc(col("notifications"), {
        message: notifMessage.trim(),
        targetRole: notifTarget,
        targetUid: "",
        read: false,
        createdAt: Timestamp.now(),
        createdBy: role || "admin",
      });
      showMsg("Notification sent.");
      setNotifOpen(false);
      setNotifMessage("");
    } catch {
      showMsg("Failed to send notification.", "error");
    }
  };

  const pendingChanges = changeRequests.filter((c) => c.status === "pending").length;

  return (
    <Box>
      {/* ─── Header ─── */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: "primary.main",
          }}
        >
          Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure payroll allowances, advances, and employee payroll settings.
        </Typography>
      </Box>

      {/* ─── Sub-tabs ─── */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={settingsTab}
          onChange={(_, v) => setSettingsTab(v)}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
          }}
        >
          <Tab value="advances" icon={<MoneyOffIcon />} iconPosition="start" label="Advances" />
          <Tab value="payroll" icon={<SettingsIcon />} iconPosition="start" label="Payroll Config" />
          {role === "admin" && (
            <Tab
              value="admin"
              icon={<SettingsIcon />}
              iconPosition="start"
              label={<Badge badgeContent={pendingChanges} color="error" max={99}><span style={{ paddingRight: 8 }}>Admin Settings</span></Badge>}
            />
          )}
        </Tabs>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* ══════ Advances Tab ══════ */}
      {settingsTab === "advances" && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <MoneyOffIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Employee Advances
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setAdvEmpId("");
                setAdvAmount(0);
                setAdvDate(dayjs().format("YYYY-MM-DD"));
                setAdvNote("");
                setAdvOpen(true);
              }}
            >
              Record Advance
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Track salary advances given to employees. Advances are deducted from the monthly payroll.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
            <TextField
              type="month"
              label="Month"
              InputLabelProps={{ shrink: true }}
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              sx={{ minWidth: 180 }}
            />
            <Typography variant="body2" color="text.secondary">
              Total:{" "}
              <strong>
                {advances
                  .reduce((s, a) => s + a.amount, 0)
                  .toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}
              </strong>{" "}
              ({advances.length} records)
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {advances.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={99} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No advances recorded for this month.
                    </TableCell>
                  </TableRow>
                )}
                {[...advPag.paginatedItems]
                  .sort((a, b) => b.date.localeCompare(a.date))
                  .map((adv) => (
                    <TableRow key={adv.id}>
                      <TableCell sx={{ fontWeight: 600 }}>{getEmpName(adv.empId)}</TableCell>
                      <TableCell>{dayjs(adv.date).format("DD/MMM/YYYY")}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {adv.amount.toLocaleString("en-IN", {
                          style: "currency",
                          currency: "INR",
                          maximumFractionDigits: 0,
                        })}
                      </TableCell>
                      <TableCell>{adv.note || "—"}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="error" onClick={() => deleteAdvance(adv.id!)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          {advances.length > 0 && (
            <TablePagination component="div" count={advPag.totalCount} page={advPag.page} onPageChange={advPag.onPageChange} rowsPerPage={advPag.rowsPerPage} onRowsPerPageChange={advPag.onRowsPerPageChange} rowsPerPageOptions={[10, 25, 50]} />
          )}
        </Paper>
      )}

      {/* ══════ Payroll Config Tab ══════ */}
      {settingsTab === "payroll" && (
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Payroll Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure per-employee payroll settings: T&D allowance percentage, allowances, and settlement account.
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
            <InputLabel>Filter by Location</InputLabel>
            <Select
              value={payrollLocFilter}
              label="Filter by Location"
              onChange={(e: SelectChangeEvent) => setPayrollLocFilter(e.target.value)}
            >
              <MenuItem value="">All Locations</MenuItem>
              {locations.map((l) => (
                <MenuItem key={l.id} value={l.id}>
                  {l.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    T&D %
                  </TableCell>
                  {[...allowanceTypes].sort((a, b) => {
                    const order = ["vehicle", "fuel", "mobile", "mobile recharge", "bonus"];
                    const ai = order.findIndex((o) => a.name.toLowerCase().includes(o));
                    const bi = order.findIndex((o) => b.name.toLowerCase().includes(o));
                    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                  }).map((at) => (
                    <TableCell key={at.id} align="right" sx={{ fontWeight: 700 }}>
                      {at.name}
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 700 }}>Account</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payConfPag.paginatedItems.map((emp) => {
                    const isEditing = payrollEditId === emp.id;
                    const accName =
                      settlementAccounts.find((a) => a.id === emp.settlementAccountId)?.name ?? "—";
                    const empName =
                      emp.firstName && emp.lastName
                        ? `${emp.firstName} ${emp.lastName}`
                        : emp.fullName || emp.empId || emp.id;
                    return (
                      <TableRow key={emp.id} hover>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: "0.75rem", bgcolor: "primary.main" }}>
                              {empName.charAt(0)}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {empName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {isEditing ? (
                            <TextField
                              type="number"
                              size="small"
                              sx={{ width: 80 }}
                              value={payrollForm.tdAllowancePct}
                              onChange={(e) =>
                                setPayrollForm({
                                  ...payrollForm,
                                  tdAllowancePct: parseFloat(e.target.value) || 0,
                                })
                              }
                              InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                              }}
                            />
                          ) : (
                            <Chip label={`${emp.tdAllowancePct ?? 10}%`} size="small" variant="outlined" />
                          )}
                        </TableCell>
                        {[...allowanceTypes].sort((a, b) => {
                          const order = ["vehicle", "fuel", "mobile", "mobile recharge", "bonus"];
                          const ai = order.findIndex((o) => a.name.toLowerCase().includes(o));
                          const bi = order.findIndex((o) => b.name.toLowerCase().includes(o));
                          return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                        }).map((at) => (
                          <TableCell key={at.id} align="right">
                            {isEditing ? (
                              <TextField
                                type="number"
                                size="small"
                                sx={{ width: 100 }}
                                value={payrollForm.allowances[at.id] || ""}
                                onChange={(e) =>
                                  setPayrollForm({
                                    ...payrollForm,
                                    allowances: {
                                      ...payrollForm.allowances,
                                      [at.id]: parseFloat(e.target.value) || 0,
                                    },
                                  })
                                }
                              />
                            ) : (
                              (emp.allowances[at.id] ?? at.amount ?? 0).toLocaleString("en-IN", {
                                style: "currency",
                                currency: "INR",
                                maximumFractionDigits: 0,
                              })
                            )}
                          </TableCell>
                        ))}
                        <TableCell>
                          {isEditing ? (
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={payrollForm.settlementAccountId}
                                onChange={(e: SelectChangeEvent) =>
                                  setPayrollForm({ ...payrollForm, settlementAccountId: e.target.value })
                                }
                              >
                                <MenuItem value="">None</MenuItem>
                                {settlementAccounts.map((sa) => (
                                  <MenuItem key={sa.id} value={sa.id}>
                                    {sa.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            accName
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {isEditing ? (
                            <>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={savePayrollConfig}
                                sx={{ mr: 0.5 }}
                              >
                                Save
                              </Button>
                              <Button size="small" onClick={() => setPayrollEditId(null)}>
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <IconButton size="small" color="primary" onClick={() => openPayrollEdit(emp)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {payrollEmps.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={99}
                      align="center"
                      sx={{ py: 3, color: "text.secondary" }}
                    >
                      No active employees found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {payrollEmps.length > 0 && (
            <TablePagination component="div" count={payConfPag.totalCount} page={payConfPag.page} onPageChange={payConfPag.onPageChange} rowsPerPage={payConfPag.rowsPerPage} onRowsPerPageChange={payConfPag.onRowsPerPageChange} rowsPerPageOptions={[10, 25, 50]} />
          )}
        </Box>
      )}

      {settingsTab === "admin" && role === "admin" && (
        <Box>
          <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Allowance Types</Typography>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => openAtDialog()}>Add Type</Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Default Amount</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allowanceTypes.length === 0 && (
                    <TableRow><TableCell colSpan={99} align="center" sx={{ py: 3, color: "text.secondary" }}>No allowance types configured.</TableCell></TableRow>
                  )}
                  {allowanceTypes.map((at) => (
                    <TableRow key={at.id}>
                      <TableCell sx={{ fontWeight: 600 }}>{at.name}</TableCell>
                      <TableCell align="right">{at.amount.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="primary" onClick={() => openAtDialog(at)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteAllowanceType(at.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AccountBalanceIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Accounts</Typography>
              </Box>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => openSaDialog()}>Add Account</Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Account Name</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {settlementAccounts.length === 0 && (
                    <TableRow><TableCell colSpan={99} align="center" sx={{ py: 3, color: "text.secondary" }}>No accounts configured.</TableCell></TableRow>
                  )}
                  {settlementAccounts.map((sa) => (
                    <TableRow key={sa.id}>
                      <TableCell sx={{ fontWeight: 600 }}>{sa.name}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="primary" onClick={() => openSaDialog(sa)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteSettlementAccount(sa.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
              Change Requests {pendingChanges > 0 && (
                <Chip label={`${pendingChanges} pending`} size="small" color="warning" sx={{ ml: 1 }} />
              )}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Attendance ID</TableCell>
                    <TableCell>Requested By</TableCell>
                    <TableCell>Requested On</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {changeRequests.length === 0 && (
                    <TableRow><TableCell colSpan={99} align="center" sx={{ py: 3, color: "text.secondary" }}>No change requests.</TableCell></TableRow>
                  )}
                  {crPag.paginatedItems.sort((a, b) => (a.status === "pending" ? -1 : 1)).map((cr) => (
                    <TableRow key={cr.id} sx={{ bgcolor: cr.status === "pending" ? "warning.light" : "inherit" }}>
                      <TableCell sx={{ fontWeight: 600 }}>{getEmpName(cr.empId)}</TableCell>
                      <TableCell sx={{ textTransform: "capitalize" }}>{cr.type.replace("_", " ")}</TableCell>
                      <TableCell sx={{ maxWidth: 250 }}><Typography variant="body2">{cr.reason}</Typography></TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{cr.attendanceId || "—"}</TableCell>
                      <TableCell>{cr.requestedBy || "—"}</TableCell>
                      <TableCell>{cr.createdAt ? dayjs(cr.createdAt.toDate()).format("DD/MMM/YYYY hh:mm A") : "—"}</TableCell>
                      <TableCell align="center">
                        <Chip label={cr.status} size="small" color={cr.status === "approved" ? "success" : cr.status === "rejected" ? "error" : "warning"} sx={{ textTransform: "capitalize", fontWeight: 600 }} />
                      </TableCell>
                      <TableCell align="center">
                        {cr.status === "pending" && (
                          <>
                            <Tooltip title="Approve"><IconButton size="small" color="success" onClick={() => resolveChangeRequest(cr.id, "approved")}><CheckCircleIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Reject"><IconButton size="small" color="error" onClick={() => resolveChangeRequest(cr.id, "rejected")}><CancelIcon fontSize="small" /></IconButton></Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {changeRequests.length > 0 && (
              <TablePagination component="div" count={crPag.totalCount} page={crPag.page} onPageChange={crPag.onPageChange} rowsPerPage={crPag.rowsPerPage} onRowsPerPageChange={crPag.onRowsPerPageChange} rowsPerPageOptions={[10, 25, 50]} />
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Send Notification</Typography>
              <Button variant="contained" size="small" onClick={() => setNotifOpen(true)}>Compose</Button>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Send in-app notifications to roles. Recipients will see them from the notifications bell.
            </Typography>
          </Paper>
        </Box>
      )}

      {/* ─── Advance Dialog ─── */}
      <Dialog
        open={advOpen}
        onClose={() => setAdvOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Record Advance</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Employee</InputLabel>
            <Select
              value={advEmpId}
              label="Employee"
              onChange={(e: SelectChangeEvent) => setAdvEmpId(e.target.value)}
            >
              {employees
                .filter((e) => e.status === "active")
                .map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.firstName && emp.lastName ? `${emp.firstName} ${emp.lastName}` : emp.fullName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={advAmount || ""}
            onChange={(e) => setAdvAmount(parseFloat(e.target.value) || 0)}
            sx={{ mb: 2 }}
          />
          <DatePicker
            label="Date"
            value={advDate ? dayjs(advDate) : null}
            onChange={(v: Dayjs | null) => setAdvDate(v ? v.format("YYYY-MM-DD") : "")}
            format="DD/MMM/YYYY"
            slotProps={{ textField: { fullWidth: true, sx: { mb: 2 } } }}
          />
          <TextField
            fullWidth
            label="Note (optional)"
            placeholder="e.g. Emergency, Medical"
            value={advNote}
            onChange={(e) => setAdvNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAdvOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveAdvance}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={atOpen} onClose={() => setAtOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{atEditId ? "Edit Allowance Type" : "Add Allowance Type"}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Name" value={atName} onChange={(e) => setAtName(e.target.value)} sx={{ mt: 1, mb: 2 }} />
          <TextField fullWidth label="Default Amount" type="number" value={atAmount || ""} onChange={(e) => setAtAmount(parseFloat(e.target.value) || 0)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAtOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveAllowanceType}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={saOpen} onClose={() => setSaOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{saEditId ? "Edit Account" : "Add Account"}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Account Name" value={saName} onChange={(e) => setSaName(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveSettlementAccount}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={notifOpen} onClose={() => setNotifOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Notification</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Target Role</InputLabel>
            <Select value={notifTarget} label="Target Role" onChange={(e: SelectChangeEvent) => setNotifTarget(e.target.value)}>
              <MenuItem value="receptionist">Front Desk</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Message"
            value={notifMessage}
            onChange={(e) => setNotifMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={sendNotification}>Send</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!msg}
        autoHideDuration={4000}
        onClose={() => setMsg("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={msgType} variant="filled" onClose={() => setMsg("")}>
          {msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
